/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import z from 'zod';
import { paginationSchema } from '../models/core';
// Imported from the leaf module rather than the `../models` barrel to keep this cycle-free.
import { bidRequestProjectTeamRoleKeys, roleToSfLookupField, } from '../models/project-team-roles';
// Cast rather than referencing `SalesforceObject.project` directly: the enum lives in the barrel
// that re-exports this module, so a value import would close a runtime cycle.
export const DEFAULT_PROJECT_OBJECT = 'Project__c';
export const MA_PROJECT_OBJECT = 'M_A_Project__c';
export const MA_PROJECT_TECHNOLOGIES = ['PV', 'W', 'DC', 'NG', 'Bio', 'S'];
const maProjectTechnologySchema = z.enum(MA_PROJECT_TECHNOLOGIES);
export function parseMAProjectTechnologies(value) {
    return value ? z.array(maProjectTechnologySchema).parse(value.split(';')) : null;
}
export function formatMAProjectTechnologies(technologies) {
    if (!technologies?.length)
        return null;
    return [...technologies]
        .sort((left, right) => MA_PROJECT_TECHNOLOGIES.indexOf(left) - MA_PROJECT_TECHNOLOGIES.indexOf(right))
        .join('+');
}
export const PROJECT_OBJECTS = new Set([
    DEFAULT_PROJECT_OBJECT,
    MA_PROJECT_OBJECT,
]);
// Built from the constants so the parsed value is typed as `ProjectObject` (i.e. the matching
// `SalesforceObject` members) rather than as bare string literals.
export const projectObjectSchema = z.enum([DEFAULT_PROJECT_OBJECT, MA_PROJECT_OBJECT]);
/**
 * Resolves a `project_object` to its non-null runtime value. A missing value resolves to
 * `Project__c` so legacy rows and inputs that omit the field behave as regular bnCRM projects.
 *
 * @throws If given a non-empty value outside the known objects, so a bad DB/input string cannot
 * silently route through the object-aware branches as if it were an M&A reference.
 */
export function normalizeProjectObject(object) {
    if (!object) {
        return DEFAULT_PROJECT_OBJECT;
    }
    if (!PROJECT_OBJECTS.has(object)) {
        throw new Error(`Unknown project_object '${object}'; expected one of ${[...PROJECT_OBJECTS].sort().join(', ')}`);
    }
    return object;
}
export function isMAProjectReference(project) {
    return normalizeProjectObject(project?.object) === MA_PROJECT_OBJECT;
}
export function isMAProjectRecord(project) {
    return project?.object === MA_PROJECT_OBJECT;
}
export const transformResultsSummary = (res) => ({
    rows: res.rows.map((result) => ({
        ...result.metrics,
        ...result.workflow,
        pvcosting_config: result.pvcosting_config ?? null,
        start_time: result.start_time,
        scenario_id: result.scenario_id,
        stage: result.stage,
        flow_name: result.flow_name,
    })),
    rowCount: res.count,
});
export const sfProjectTeamLookupFields = bidRequestProjectTeamRoleKeys.map((role) => roleToSfLookupField[role]);
export const projectTeamLookupFields = [
    'Id',
    'Name',
    ...sfProjectTeamLookupFields.map((x) => `${x}.Email`),
    ...sfProjectTeamLookupFields.map((x) => `${x}.Name`),
];
export const projectResultsQueryParamsSchema = paginationSchema.extend({
    bod: z.boolean().optional(),
    dl: z.enum(['xlsx']).optional(),
    selectedIds: z.string().nonempty().optional(),
});
export const projectWeatherQueryParamsSchema = paginationSchema.extend({
    radius: z.number().min(0).default(50000),
    showArchived: z.boolean().default(false),
    hasRainfall: z.boolean().default(false),
    bod: z.boolean().optional(),
});
//# sourceMappingURL=projects.js.map